// RoomList — the searchable, sectioned list of rooms in the sidebar.
//
// Reads the active-space-scoped list from `useRooms`, runs it through
// `room-utils` (filter → categorise → sort), and renders the
// collapsible sections: either all Spaces plus unparented rooms, or
// the channels inside the selected Space. Empty and filtered-empty
// states live here so the Sidebar stays a thin shell.
//
// Scope note: the brief mentions a resizable divider and a collapsed
// icon rail. Both are deferred to their own tickets — this module owns
// the list at its default width and leaves width concerns to the
// parent Sidebar.

import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { ChevronDown, MoreHorizontal, Plus, Search } from "lucide-react";

import { PointerMenu, type PointerMenuItem } from "~/components/PointerMenu";
import { RoomListItem } from "~/components/RoomListItem";
import { IconTooltip } from "~/components/IconTooltip";
import { useMatrix, useMatrixRooms } from "~/context/MatrixContext";
import { useSidebarLayout } from "~/hooks/useSidebarLayout";
import { listDraftKeys, subscribeDraftKeys } from "~/lib/drafts";
import {
  applySectionOrder,
  DESKTOP_SECTION_ORDER,
  foldDirectDuplicates,
  isRoomHidden,
  type SidebarSectionId,
} from "~/lib/sidebar-layout";
import { getMembers, getRoomNotificationLevel, type RoomSummary } from "~/lib/matrix";
import {
  categorize,
  filterRooms,
  sortByActivity,
  type CategorisedRooms,
  type RoomMembersLookup,
} from "~/lib/room-utils";
import {
  deriveSpaceHierarchy,
  groupRoomsBySpace,
  repairSpaceSelection,
  visibleRoomIdsForSpace,
  visibleRoomsForSpace,
} from "~/lib/spaces";
import { shortcutHintsEnabled } from "~/lib/platform";
import { globalSearchHintLabel } from "~/lib/shortcuts";
import { useOrgDmMembership } from "~/hooks/useOrgDmMembership";
import { ICON_STROKE } from "~/lib/icon";

const SEARCH_SHORTCUT_LABEL = globalSearchHintLabel();
const SHOW_SEARCH_SHORTCUT_HINT = shortcutHintsEnabled();

// Hover-reveal for the section-header `+` button. Injected once on
// module load so the section doesn't mutate document during render.
const SECTION_STYLE_ID = "chat-sidebar-section-styles";
function injectSectionStyles(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(SECTION_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = SECTION_STYLE_ID;
  style.textContent = `
    .chat-section-header__add {
      opacity: 0;
      transition: opacity var(--chat-motion-fast, 120ms) var(--chat-motion-ease, ease);
    }
    .chat-sidebar-section:hover .chat-section-header__add,
    .chat-section-header__add:focus-visible {
      opacity: 1;
    }
  `;
  document.head.appendChild(style);
}
injectSectionStyles();

// Collapsed-section state is persisted under a single key. Storing the
// whole map (rather than one key per section) keeps the localStorage
// surface tiny and leaves one place to clear on sign-out.
export const COLLAPSED_SECTIONS_KEY = "chat.sidebar.sections";

// Section keys: the two static buckets, plus one `space:<roomId>`
// entry per joined Space. Spaces are dynamic so the map is open-ended;
// values must always be booleans.
type SectionId = "channels" | "dms" | "hidden" | `space:${string}`;
type CollapsedMap = Partial<Record<SectionId, boolean>>;

function readCollapsed(): CollapsedMap {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(COLLAPSED_SECTIONS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    // Narrow defensively — a stale value written by an older build
    // (or a user hand-editing localStorage) shouldn't crash the
    // sidebar. Accept the two static keys plus any `space:<id>` key
    // a previous boot persisted; ignore everything else.
    const source = parsed as Record<string, unknown>;
    const next: CollapsedMap = {};
    if (typeof source.channels === "boolean") next.channels = source.channels;
    if (typeof source.dms === "boolean") next.dms = source.dms;
    if (typeof source.hidden === "boolean") next.hidden = source.hidden;
    for (const [key, value] of Object.entries(source)) {
      if (typeof value !== "boolean") continue;
      if (!key.startsWith("space:")) continue;
      next[key as SectionId] = value;
    }
    return next;
  } catch {
    return {};
  }
}

function writeCollapsed(map: CollapsedMap): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(COLLAPSED_SECTIONS_KEY, JSON.stringify(map));
  } catch {
    // Quota / disabled storage — persistence is a best-effort nicety.
  }
}

export interface RoomListProps {
  activeOrgSpaceId?: string | null;
  activeSpaceId?: string | null;
  /** Opens the create-channel dialog. Passed through to the Channels
   *  section header and the empty-state CTA. */
  onCreateRoom?: () => void;
  /** Opens the new-DM dialog. Passed through to the DMs section
   *  header. */
  onNewDm?: () => void;
  /** Opens the global-search palette seeded with the given query. The
   *  bridge row, the search box's Enter key, and the ⌘K button all
   *  route here; the palette is never opened by typing alone. */
  onOpenSearch?: (query: string) => void;
}

export function RoomList({
  activeOrgSpaceId = null,
  activeSpaceId = null,
  onCreateRoom,
  onNewDm,
  onOpenSearch,
}: RoomListProps) {
  const {
    activeRoomId,
    setActiveRoomId,
    setChannelType,
    state,
    roomsReady,
    activeView,
    client,
    userId,
  } = useMatrix();
  const allRooms = useMatrixRooms();
  const hierarchy = useMemo(() => deriveSpaceHierarchy(allRooms), [allRooms]);
  const repairedSelection = useMemo(
    () =>
      repairSpaceSelection(
        allRooms,
        activeOrgSpaceId,
        activeSpaceId && activeSpaceId !== activeOrgSpaceId ? activeSpaceId : null,
      ),
    [allRooms, activeOrgSpaceId, activeSpaceId],
  );
  const effectiveOrgSpaceId = repairedSelection.orgSpaceId;
  const effectiveSpaceId = repairedSelection.activeSpaceId;
  const rooms = useMemo(
    () =>
      visibleRoomsForSpace(allRooms, effectiveSpaceId ?? effectiveOrgSpaceId).filter(
        (room) => room.archived === null,
      ),
    [allRooms, effectiveOrgSpaceId, effectiveSpaceId],
  );
  const visibleRooms = useMemo(() => rooms.filter((room) => !room.isSpace), [rooms]);
  // `filterRooms` calls this lazily, only while a query is active, so an
  // empty query never reads the member cache.
  const lookupMembers = useCallback<RoomMembersLookup>(
    (roomId) => (client ? getMembers(client, roomId) : []),
    [client],
  );
  // Read the muted state for each joined room once per render. The
  // Matrix wrapper's level lookup is synchronous on the stub and
  // cheap on the real SDK (reads pre-parsed push-rule state). The
  // subscription model already fires `subscribeRooms` on every
  // push-rule override, so this picks up a mute/unmute on the next
  // render after the write completes.
  const mutedRoomIds = useMemo(() => {
    if (!client) return new Set<string>();
    const out = new Set<string>();
    for (const room of visibleRooms) {
      if (getRoomNotificationLevel(client, room.id) === "mute") out.add(room.id);
    }
    return out;
  }, [client, visibleRooms]);
  // A room is visually "active" only when the timeline is the view on
  // screen. When the user is in Saved, no room row should wear the
  // active bar — otherwise the sidebar tells them two things are
  // selected at once.
  const roomIsActiveView = activeView === "room";

  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState<CollapsedMap>(() => readCollapsed());
  // Track which `(roomId, 'main')` keys currently have a stored
  // draft so each room row can decorate itself accordingly. Threaded
  // drafts are intentionally excluded — the brief routes them to the
  // thread preview, not the room row.
  const [draftRoomIds, setDraftRoomIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    writeCollapsed(collapsed);
  }, [collapsed]);

  useEffect(() => {
    let cancelled = false;
    const refresh = () => {
      void listDraftKeys().then((keys) => {
        if (cancelled) return;
        const next = new Set<string>();
        for (const key of keys) {
          if (!userId || !key.startsWith(`${userId}::`)) continue;
          const sep = key.lastIndexOf("::");
          if (sep < 0) continue;
          const suffix = key.slice(sep + 2);
          if (suffix !== "main") continue;
          const roomId = key.slice(userId.length + 2, sep);
          next.add(roomId);
        }
        setDraftRoomIds(next);
      });
    };
    refresh();
    const unsubscribe = subscribeDraftKeys(refresh);
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [userId]);

  const hasExplicitScope = activeOrgSpaceId !== null || activeSpaceId !== null;
  const orgDm = useOrgDmMembership(hasExplicitScope ? effectiveOrgSpaceId : null, allRooms);
  const dmStatus: "pending" | "ready" | "empty" | "error" = orgDm.all
    ? "empty"
    : orgDm.orgRoster.status === "error"
      ? "error"
      : orgDm.orgRoster.status === "pending"
        ? "pending"
        : [...orgDm.dmEligibleByRoomId.values()].some(Boolean)
          ? "ready"
          : [...orgDm.dmPendingByRoomId.values()].some(Boolean)
            ? "error"
            : "empty";
  const scopeDm = useCallback(
    (list: RoomSummary[]) =>
      orgDm.all
        ? list
        : list.filter(
            (room) =>
              !orgDm.dmPendingByRoomId.get(room.id) &&
              orgDm.dmEligibleByRoomId.get(room.id) === true,
          ),
    [orgDm],
  );

  // filter → categorise → group-by-space → sort inside each bucket.
  // A memo keeps the reference stable across renders so
  // `RoomListItem` isn't needlessly reconciled when only the query
  // changes.
  const { channels, spaceGroups, dms } = useMemo(() => {
    const filtered = filterRooms(visibleRooms, query, lookupMembers);
    const cats: CategorisedRooms = categorize(filtered);

    const scopeSpaceId = effectiveSpaceId ?? effectiveOrgSpaceId;
    if (scopeSpaceId !== null && scopeSpaceId !== effectiveOrgSpaceId) {
      const childIds = visibleRoomIdsForSpace(allRooms, scopeSpaceId);
      return {
        channels: sortByActivity(cats.channels.filter((room) => childIds.has(room.id))),
        spaceGroups: [],
        dms: scopeDm(foldDirectDuplicates(sortByActivity(cats.dms))),
      };
    }

    const childSpacesForOrg = effectiveOrgSpaceId
      ? (hierarchy.childSpacesByOrgSpaceId.get(effectiveOrgSpaceId) ?? [])
      : [];
    const grouped = groupRoomsBySpace(cats.channels, childSpacesForOrg);
    return {
      channels: sortByActivity(grouped.unparented),
      spaceGroups: grouped.spaces.map((group) => ({
        space: group.space,
        children: sortByActivity(group.children),
      })),
      dms: scopeDm(foldDirectDuplicates(sortByActivity(cats.dms))),
    };
  }, [
    visibleRooms,
    query,
    lookupMembers,
    allRooms,
    effectiveOrgSpaceId,
    effectiveSpaceId,
    hierarchy,
    scopeDm,
  ]);

  const isFiltering = query.trim().length > 0;
  const { layout, setOrder, resetOrder, setHidden } = useSidebarLayout(DESKTOP_SECTION_ORDER);
  const [dragging, setDragging] = useState<SidebarSectionId | null>(null);
  const [dropTarget, setDropTarget] = useState<{ id: SidebarSectionId; after: boolean } | null>(
    null,
  );
  const [menu, setMenu] = useState<{
    x: number;
    y: number;
    label: string;
    items: PointerMenuItem[];
  } | null>(null);
  const closeMenu = useCallback(() => setMenu(null), []);

  // Hidden rooms leave their section for a collapsed "Hidden" one at the
  // end; while filtering, everything that matches shows in place.
  const shown = useCallback(
    (list: RoomSummary[]) =>
      isFiltering ? list : list.filter((room) => !isRoomHidden(layout, room)),
    [layout, isFiltering],
  );
  const hiddenRooms = useMemo(
    () =>
      isFiltering
        ? []
        : [...spaceGroups.flatMap((group) => group.children), ...channels, ...dms].filter((room) =>
            isRoomHidden(layout, room),
          ),
    [spaceGroups, channels, dms, layout, isFiltering],
  );

  const openRoomMenu = useCallback(
    (room: RoomSummary, event: React.MouseEvent) => {
      event.preventDefault();
      const hidden = layout.hidden.includes(room.id);
      setMenu({
        x: event.clientX,
        y: event.clientY,
        label: `Actions for ${room.isDirect ? room.name : `#${room.name}`}`,
        items: [
          {
            key: "hide",
            label: hidden ? "Show in sidebar" : "Hide",
            onSelect: () => setHidden(room.id, !hidden),
          },
        ],
      });
    },
    [layout.hidden, setHidden],
  );

  const toggle = useCallback((id: SectionId) => {
    setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const openPalette = useCallback(() => {
    onOpenSearch?.(query);
  }, [onOpenSearch, query]);

  const handleTakeOver = useCallback(
    (room: RoomSummary) => {
      // The affordance is already disabled when the user lacks the
      // power level (`TakeOverPill` renders `disabled`), but this is a
      // last-line guard — a keyboard user tabbing through an
      // "impossible" state still can't fire a write the server would
      // refuse.
      if (!room.canSetChannelType) return;
      // Errors are logged; a user-facing toast would duplicate the
      // ToastContext's surface and isn't wired through this component
      // yet. When the toast seam lands in a follow-up, callers of
      // `setChannelType` should show a "Couldn't take over — retry"
      // error with the room name.
      void setChannelType(room.id, "mixed").catch((err) => {
        console.error("setChannelType failed", err);
      });
    },
    [setChannelType],
  );

  const handleRowContextMenu = useCallback(
    (event: React.MouseEvent, room: RoomSummary) => openRoomMenu(room, event),
    [openRoomMenu],
  );
  const hasRooms = visibleRooms.length > 0;
  const spacedChannelCount = spaceGroups.reduce((sum, group) => sum + group.children.length, 0);
  const totalVisible = channels.length + dms.length + spacedChannelCount;
  const isLoading = !hasRooms && (state === "loading" || state === "syncing" || !roomsReady);

  const sectionOrder = applySectionOrder(layout.order, [
    "dms",
    ...spaceGroups.map(({ space }): SidebarSectionId => `space:${space.id}`),
    "channels",
  ]);

  const moveSectionTo = (id: SidebarSectionId, target: SidebarSectionId, after: boolean) => {
    if (id === target) return;
    const next = sectionOrder.filter((entry) => entry !== id);
    next.splice(next.indexOf(target) + (after ? 1 : 0), 0, id);
    setOrder(next);
  };

  const openSectionMenu = (
    id: SidebarSectionId,
    label: string,
    event: React.MouseEvent<HTMLElement>,
  ) => {
    event.preventDefault();
    const index = sectionOrder.indexOf(id);
    const above = sectionOrder[index - 1];
    const below = sectionOrder[index + 1];
    // A keyboard-activated button reports (0, 0); anchor the menu to the button instead.
    const rect = event.currentTarget.getBoundingClientRect();
    const fromKeyboard = event.clientX === 0 && event.clientY === 0;
    setMenu({
      x: fromKeyboard ? rect.left : event.clientX,
      y: fromKeyboard ? rect.bottom : event.clientY,
      label: `Arrange ${label}`,
      items: [
        {
          key: "up",
          label: "Move up",
          disabled: !above,
          onSelect: () => above && moveSectionTo(id, above, false),
        },
        {
          key: "down",
          label: "Move down",
          disabled: !below,
          onSelect: () => below && moveSectionTo(id, below, true),
        },
        { key: "reset", label: "Reset order", onSelect: resetOrder },
      ],
    });
  };

  const sectionDrag = (id: SidebarSectionId): SectionDrag | undefined =>
    isFiltering
      ? undefined
      : {
          indicator:
            dragging && dropTarget?.id === id ? (dropTarget.after ? "after" : "before") : null,
          onDragStart: (event) => {
            event.dataTransfer.effectAllowed = "move";
            event.dataTransfer.setData("text/plain", id);
            setDragging(id);
          },
          onDragOver: (event) => {
            if (!dragging) return;
            event.preventDefault();
            const rect = event.currentTarget.getBoundingClientRect();
            const after = event.clientY > rect.top + rect.height / 2;
            if (dropTarget?.id !== id || dropTarget.after !== after) setDropTarget({ id, after });
          },
          onDrop: (event) => {
            event.preventDefault();
            if (dragging && dropTarget) moveSectionTo(dragging, dropTarget.id, dropTarget.after);
            setDragging(null);
            setDropTarget(null);
          },
          onDragEnd: () => {
            setDragging(null);
            setDropTarget(null);
          },
        };

  const renderOrderedSection = (sectionId: SidebarSectionId) =>
    sectionId === "channels"
      ? (channels.length > 0 || (onCreateRoom && !isFiltering)) && (
          <Section
            key="channels"
            id="channels"
            label="Channels"
            collapsed={Boolean(collapsed.channels)}
            onToggle={() => toggle("channels")}
            onArrange={(event) => openSectionMenu("channels", "Channels", event)}
            drag={sectionDrag("channels")}
            onAdd={onCreateRoom}
            addLabel="Create channel"
          >
            {shown(channels).map((room) => (
              <RoomListItem
                key={room.id}
                room={room}
                active={room.id === activeRoomId && roomIsActiveView}
                onActivate={setActiveRoomId}
                onContextMenu={handleRowContextMenu}
                onTakeOver={handleTakeOver}
                muted={mutedRoomIds.has(room.id)}
                hasDraft={draftRoomIds.has(room.id)}
              />
            ))}
          </Section>
        )
      : (dms.length > 0 || (!orgDm.all && dms.length === 0) || (onNewDm && !isFiltering)) && (
          <Section
            key="dms"
            id="dms"
            label="Direct messages"
            collapsed={Boolean(collapsed.dms)}
            onToggle={() => toggle("dms")}
            onArrange={(event) => openSectionMenu("dms", "Direct messages", event)}
            drag={sectionDrag("dms")}
            onAdd={onNewDm}
            addLabel="New direct message"
          >
            {dms.length > 0 ? (
              <div data-cy="org-dm-list">
                {shown(dms).map((room) => (
                  <RoomListItem
                    key={room.id}
                    room={room}
                    active={room.id === activeRoomId && roomIsActiveView}
                    onActivate={setActiveRoomId}
                    onContextMenu={handleRowContextMenu}
                    muted={mutedRoomIds.has(room.id)}
                    hasDraft={draftRoomIds.has(room.id)}
                  />
                ))}
              </div>
            ) : (
              !orgDm.all && <DmScopeStatus status={dmStatus} onRetry={orgDm.retry} />
            )}
          </Section>
        );

  const renderSection = (sectionId: SidebarSectionId) => {
    if (sectionId === "channels" || sectionId === "dms") return renderOrderedSection(sectionId);
    const group = spaceGroups.find(({ space }) => `space:${space.id}` === sectionId);
    if (!group) return null;
    const { space, children } = group;
    const label = space.name.length > 0 ? space.name : "Space";
    return (
      <Section
        key={sectionId}
        id={sectionId}
        label={label}
        collapsed={Boolean(collapsed[sectionId])}
        onToggle={() => toggle(sectionId)}
        onArrange={(event) => openSectionMenu(sectionId, label, event)}
        drag={sectionDrag(sectionId)}
        indented
      >
        {shown(children).map((room) => (
          <RoomListItem
            key={room.id}
            room={room}
            active={room.id === activeRoomId && roomIsActiveView}
            onActivate={setActiveRoomId}
            onContextMenu={handleRowContextMenu}
            onTakeOver={handleTakeOver}
            muted={mutedRoomIds.has(room.id)}
            hasDraft={draftRoomIds.has(room.id)}
          />
        ))}
      </Section>
    );
  };

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <SearchField
        value={query}
        onChange={setQuery}
        onSubmit={openPalette}
        onOpenPalette={onOpenSearch ? openPalette : undefined}
      />
      {isLoading ? (
        <RoomListShimmer />
      ) : (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            padding: "var(--chat-space-2) 0",
            display: "flex",
            flexDirection: "column",
            gap: "var(--chat-space-2)",
          }}
        >
          {totalVisible === 0 && (
            <RoomListEmpty
              primary={
                isFiltering
                  ? `No rooms match "${query.trim()}"`
                  : effectiveOrgSpaceId
                    ? "No rooms in this space"
                    : "No rooms yet"
              }
              secondary={
                isFiltering
                  ? onOpenSearch
                    ? "Search all messages below."
                    : "Try a different name."
                  : effectiveOrgSpaceId
                    ? "Pick another space."
                    : "Create one to get started."
              }
            />
          )}
          {sectionOrder.map(renderSection)}
          {hiddenRooms.length > 0 && (
            <Section
              id="hidden"
              label={`Hidden · ${hiddenRooms.length}`}
              collapsed={collapsed.hidden !== false}
              onToggle={() => setCollapsed((prev) => ({ ...prev, hidden: prev.hidden === false }))}
            >
              {hiddenRooms.map((room) => (
                <RoomListItem
                  key={room.id}
                  room={room}
                  active={room.id === activeRoomId && roomIsActiveView}
                  onActivate={setActiveRoomId}
                  onContextMenu={handleRowContextMenu}
                  muted={mutedRoomIds.has(room.id)}
                  hasDraft={draftRoomIds.has(room.id)}
                />
              ))}
            </Section>
          )}
          {isFiltering && onOpenSearch && (
            <SearchAllMessagesRow query={query.trim()} onActivate={openPalette} />
          )}
        </div>
      )}
      {menu && (
        <PointerMenu
          x={menu.x}
          y={menu.y}
          label={menu.label}
          items={menu.items}
          onClose={closeMenu}
        />
      )}
    </div>
  );
}

/* ─── sub-components ────────────────────────────────────────────── */

function SearchField({
  value,
  onChange,
  onSubmit,
  onOpenPalette,
}: {
  value: string;
  onChange: (next: string) => void;
  /** Enter inside the box escalates a non-empty query to the palette. */
  onSubmit?: () => void;
  /** Opens the palette from the ⌘K button. Omitted when no palette
   *  handler is wired (e.g. Storybook) so the chip stays inert. */
  onOpenPalette?: () => void;
}) {
  // The input carries its own `aria-label="Search rooms"` so a wrapper
  // `<label>` would duplicate (and the icon + ⌘K control would leak
  // into the accessible name). A plain flex container is the honest shape.
  return (
    <div
      style={{
        padding: "var(--chat-space-2) var(--chat-space-3)",
        flexShrink: 0,
      }}
    >
      <div
        className="chat-composer-wrapper"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--chat-space-2)",
          height: "var(--chat-sidebar-search-height)",
          padding: "0 var(--chat-space-3)",
          borderRadius: 0,
        }}
      >
        <Search
          aria-hidden
          size={14}
          strokeWidth={ICON_STROKE}
          style={{ color: "var(--chat-fg-secondary)", flexShrink: 0 }}
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== "Enter" || value.trim().length === 0) return;
            e.preventDefault();
            onSubmit?.();
          }}
          placeholder="Search or jump to…"
          aria-label="Search rooms"
          className="placeholder:text-[hsl(var(--foreground)/0.2)]"
          style={{
            flex: 1,
            minWidth: 0,
            border: "none",
            outline: "none",
            background: "transparent",
            color: "var(--chat-fg-primary)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-sm-size)",
            lineHeight: "var(--chat-type-body-sm-line)",
          }}
        />
        <button
          type="button"
          onClick={onOpenPalette}
          disabled={!onOpenPalette}
          aria-label="Search all messages"
          className="chat-button enabled:hover:text-[var(--chat-fg-secondary)]"
          style={{
            padding: 0,
            border: "none",
            background: "transparent",
            color: "var(--chat-fg-muted)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-xs-size)",
            lineHeight: "var(--chat-type-body-xs-line)",
            flexShrink: 0,
            cursor: onOpenPalette ? "pointer" : "default",
          }}
        >
          {SHOW_SEARCH_SHORTCUT_HINT ? (
            SEARCH_SHORTCUT_LABEL
          ) : (
            <Search
              aria-hidden
              size={14}
              strokeWidth={ICON_STROKE}
              style={{ color: "var(--chat-fg-muted)" }}
            />
          )}
        </button>
      </div>
    </div>
  );
}

function SearchAllMessagesRow({ query, onActivate }: { query: string; onActivate: () => void }) {
  // No inline `background` — the `chat-search-result` class owns the
  // rest/hover/active surface; an inline value would defeat its `:hover`.
  return (
    <div style={{ padding: "0 var(--chat-space-2)" }}>
      <button
        type="button"
        onClick={onActivate}
        className="chat-search-result chat-button"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--chat-space-2)",
          width: "100%",
          height: "var(--chat-row-comfortable-channel)",
          padding: "0 var(--chat-space-3)",
          border: "none",
          color: "var(--chat-fg-secondary)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-sm-size)",
          lineHeight: "var(--chat-type-body-sm-line)",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <Search aria-hidden size={14} strokeWidth={ICON_STROKE} style={{ flexShrink: 0 }} />
        <span
          style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          Search all messages for “{query}”
        </span>
      </button>
    </div>
  );
}

interface SectionProps {
  id: SectionId;
  label: string;
  collapsed: boolean;
  onToggle: () => void;
  /** Header ⋯ button or right-click: Move up / Move down / Reset order. */
  onArrange?: (event: React.MouseEvent<HTMLElement>) => void;
  /** Drag-to-reorder handlers; absent while filtering. */
  drag?: SectionDrag;
  onAdd?: () => void;
  addLabel?: string;
  /** Nest the row list under a Space header. Adds one extra
   *  spacing-token unit of left inset (`--chat-space-1` on top of
   *  the row list's existing `--chat-space-2` padding) so children
   *  read as belonging to the Space without crowding the 260px
   *  sidebar. */
  indented?: boolean;
  children: React.ReactNode;
}

// The list container is a plain `<div>` — each `RoomListItem` renders one
// activation button plus its secondary controls (siblings, not nested).
// Adding a list landmark would force every item to wrap in a `<li>` /
// `role="listitem"` for ARIA validity, which adds markup without changing
// how AT users reach the rows.
const rowListStyle: CSSProperties = {
  margin: 0,
  padding: "0 var(--chat-space-2)",
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

const indentedRowListStyle: CSSProperties = {
  ...rowListStyle,
  paddingLeft: "calc(var(--chat-space-2) + var(--chat-space-1))",
};

interface SectionDrag {
  indicator: "before" | "after" | null;
  onDragStart: (event: React.DragEvent<HTMLElement>) => void;
  onDragOver: (event: React.DragEvent<HTMLElement>) => void;
  onDrop: (event: React.DragEvent<HTMLElement>) => void;
  onDragEnd: () => void;
}

function Section({
  id,
  label,
  collapsed,
  onToggle,
  onArrange,
  drag,
  onAdd,
  addLabel,
  indented,
  children,
}: SectionProps) {
  const headingId = `chat-sidebar-section-${id}`;
  const listId = `${headingId}-list`;
  return (
    <section
      aria-labelledby={headingId}
      className="chat-sidebar-section"
      data-section-id={id}
      onDragOver={drag?.onDragOver}
      onDrop={drag?.onDrop}
      style={{ position: "relative" }}
    >
      {drag?.indicator && (
        <span
          aria-hidden
          data-drop-indicator={drag.indicator}
          style={{
            position: "absolute",
            left: "var(--chat-space-3)",
            right: "var(--chat-space-3)",
            [drag.indicator === "before" ? "top" : "bottom"]: -1,
            height: 2,
            borderRadius: 1,
            background: "var(--chat-accent)",
          }}
        />
      )}
      <SectionHeader
        draggable={Boolean(drag)}
        onDragStart={drag?.onDragStart}
        onDragEnd={drag?.onDragEnd}
        id={headingId}
        listId={listId}
        label={label}
        collapsed={collapsed}
        onToggle={onToggle}
        onArrange={onArrange}
        onAdd={onAdd}
        addLabel={addLabel}
      />
      {!collapsed && (
        <div id={listId} style={indented ? indentedRowListStyle : rowListStyle}>
          {children}
        </div>
      )}
    </section>
  );
}

interface SectionHeaderProps {
  id: string;
  listId: string;
  label: string;
  collapsed: boolean;
  onToggle: () => void;
  onArrange?: (event: React.MouseEvent<HTMLElement>) => void;
  draggable?: boolean;
  onDragStart?: (event: React.DragEvent<HTMLElement>) => void;
  onDragEnd?: () => void;
  onAdd?: () => void;
  addLabel?: string;
}

function SectionHeader({
  id,
  listId,
  label,
  collapsed,
  onToggle,
  onArrange,
  draggable,
  onDragStart,
  onDragEnd,
  onAdd,
  addLabel,
}: SectionHeaderProps) {
  // Interactive controls must be siblings, not nested — a screen
  // reader announces a nested button as "button inside button" and
  // keyboard users can tab into controls that aren't obviously
  // interactive. Split the header into a toggle button that covers the
  // chevron + label area and an adjacent add button.
  return (
    <div
      className="chat-sidebar-section-header"
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      style={{
        display: "flex",
        alignItems: "center",
        height: "var(--chat-sidebar-section-header-height)",
      }}
    >
      <button
        type="button"
        id={id}
        aria-expanded={!collapsed}
        aria-controls={listId}
        onClick={onToggle}
        onContextMenu={onArrange}
        className="chat-button"
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          gap: "var(--chat-space-1)",
          height: "100%",
          padding: "0 var(--chat-space-3)",
          border: "none",
          background: "transparent",
          color: "var(--chat-fg-readable)",
          fontFamily: "var(--chat-font-sans)",
          // Match the Activity section header treatment so all section
          // labels read at the same hierarchy level.
          fontSize: 10,
          lineHeight: "12px",
          textTransform: "uppercase",
          letterSpacing: "0.1em",
          cursor: "pointer",
          userSelect: "none",
          textAlign: "left",
        }}
      >
        <ChevronDown
          aria-hidden
          size={10}
          strokeWidth={ICON_STROKE}
          style={{
            transition: "transform var(--chat-motion-fast) var(--chat-motion-ease)",
            transform: collapsed ? "rotate(-90deg)" : "none",
            flexShrink: 0,
            opacity: 0.6,
          }}
        />
        <span style={{ flex: 1 }}>{label}</span>
      </button>
      {onArrange && (
        <IconTooltip label={`Arrange ${label}`}>
          <button
            type="button"
            onClick={onArrange}
            aria-label={`Arrange ${label}`}
            aria-haspopup="menu"
            className="chat-section-header__add chat-button"
            style={{
              width: 24,
              height: 24,
              marginRight: onAdd ? 0 : "var(--chat-space-2)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              border: "none",
              borderRadius: "var(--chat-radius-sm)",
              background: "transparent",
              color: "var(--chat-fg-secondary)",
              cursor: "pointer",
              padding: 0,
              flexShrink: 0,
            }}
          >
            <MoreHorizontal aria-hidden size={12} strokeWidth={ICON_STROKE} />
          </button>
        </IconTooltip>
      )}
      {onAdd && (
        <IconTooltip label={addLabel ?? `Add ${label}`}>
          <button
            type="button"
            onClick={onAdd}
            aria-label={addLabel ?? `Add ${label}`}
            className="chat-section-header__add chat-button"
            style={{
              width: 24,
              height: 24,
              marginRight: "var(--chat-space-2)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              border: "none",
              borderRadius: "var(--chat-radius-sm)",
              background: "transparent",
              color: "var(--chat-fg-secondary)",
              cursor: "pointer",
              padding: 0,
              flexShrink: 0,
            }}
          >
            <Plus aria-hidden size={12} strokeWidth={ICON_STROKE} />
          </button>
        </IconTooltip>
      )}
    </div>
  );
}

/* ─── loading skeleton ─────────────────────────────────────────── */

function RoomListShimmer() {
  return (
    <div
      aria-hidden
      data-testid="room-list-shimmer"
      style={{
        flex: 1,
        padding: "var(--chat-space-2)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--chat-space-1)",
      }}
    >
      {[0, 1, 2].map((index) => (
        <div
          key={index}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--chat-space-2)",
            height: "var(--chat-row-comfortable-channel)",
            padding: "0 var(--chat-space-3)",
          }}
        >
          <span
            style={{
              width: 14,
              height: 14,
              borderRadius: "var(--chat-radius-sm)",
              background: "var(--chat-surface-raised)",
              flexShrink: 0,
            }}
          />
          <span
            style={{
              flex: 1,
              // Vary width so the rows don't look too uniform while
              // keeping the total silhouette honest.
              maxWidth: `${60 + index * 10}%`,
              height: 10,
              borderRadius: "var(--chat-radius-sm)",
              background: "var(--chat-surface-raised)",
            }}
          />
        </div>
      ))}
    </div>
  );
}

// Pending/ready/empty/error copy for the scoped DM section; mirrors mobile
// `HomeOrgStatus` so the two surfaces agree and never fall back to all-DMs.
function DmScopeStatus({
  status,
  onRetry,
}: {
  status: "pending" | "ready" | "empty" | "error";
  onRetry: () => void;
}) {
  const message =
    status === "pending"
      ? "Checking organisation members…"
      : status === "error"
        ? "Could not load organisation members."
        : "No conversations in this organisation.";
  return (
    <div style={{ padding: "var(--chat-space-2) var(--chat-space-3)", textAlign: "center" }}>
      <p
        data-cy="org-dm-status"
        style={{
          color: "var(--chat-fg-secondary)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-sm-size)",
          lineHeight: "var(--chat-type-body-sm-line)",
          margin: 0,
        }}
      >
        {message}
      </p>
      {status === "error" && (
        <button
          type="button"
          data-cy="org-dm-retry"
          onClick={onRetry}
          className="chat-button"
          style={{
            marginTop: "var(--chat-space-2)",
            height: "var(--chat-control-h)",
            padding: "0 var(--chat-space-3)",
            borderRadius: "var(--chat-radius-sm)",
            border: "1px solid var(--chat-border)",
            background: "var(--chat-fg-subtle)",
            color: "var(--chat-fg)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-button-size)",
            cursor: "pointer",
          }}
        >
          Retry
        </button>
      )}
    </div>
  );
}

function RoomListEmpty({
  primary,
  secondary,
  cta,
}: {
  primary: string;
  secondary: string;
  cta?: { label: string; onClick: () => void };
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "var(--chat-space-2)",
        flex: 1,
        padding: "var(--chat-space-6)",
        textAlign: "center",
      }}
    >
      <p
        style={{
          color: "var(--chat-fg-primary)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-md-size)",
          lineHeight: "var(--chat-type-body-md-line)",
          fontWeight: 400,
          margin: 0,
        }}
      >
        {primary}
      </p>
      <p
        style={{
          color: "var(--chat-fg-secondary)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-sm-size)",
          lineHeight: "var(--chat-type-body-sm-line)",
          margin: 0,
        }}
      >
        {secondary}
      </p>
      {cta ? (
        <button
          type="button"
          onClick={cta.onClick}
          className="chat-button"
          style={{
            marginTop: "var(--chat-space-2)",
            padding: "var(--chat-space-2) var(--chat-space-5)",
            borderRadius: "var(--chat-radius-lg)",
            border: "none",
            background: "var(--chat-fg-primary)",
            color: "var(--chat-surface-app)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-sm-size)",
            lineHeight: "var(--chat-type-body-sm-line)",
            fontWeight: 500,
            cursor: "pointer",
            transition: "opacity var(--chat-motion-fast) var(--chat-motion-ease)",
          }}
        >
          {cta.label}
        </button>
      ) : null}
    </div>
  );
}
